import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faUser, faTag, faArrowLeft, faShare } from '@fortawesome/free-solid-svg-icons';
import './Blog.css';

const BlogDetail = () => {
  const { id } = useParams();

  // Mock blog posts data (same as in Blog.js)
  const mockPosts = [
{
    "id": 19,
    "title": "Lena Medical Telehealth: See an AHPRA-Registered Doctor Online, Anywhere in Australia",
    "excerpt": "Book an online doctor consultation with Lena Medical Telehealth. AHPRA-registered GPs, specialists & allied health. Bulk billing available. Australia-wide.",
    "image": "/images/lena-medical-telehealth-australia.png",
    "date": "22 August 2026",
    "author": "Tea Garden GP Team",
    "category": "General Practice",
    "tags": ["Tea Gardens", "health"],
    "metaTitle": "Telehealth GP Australia | Online Doctor | AHPRA-Registered | Lena Medical Telehealth",
    "metaDescription": "Book an online doctor consultation with Lena Medical Telehealth. AHPRA-registered GPs, specialists & allied health. Bulk billing available. Australia-wide.",
    "content": "Tea Gardens GP partners with Lena Medical Telehealth to help patients access quality healthcare when an in-person clinic visit is not practical. Whether you're at home with a sick child, working remotely, managing a chronic condition, or short on time, our telehealth partner connects patients with qualified healthcare professionals across Australia.\n\n## What Is Lena Medical Telehealth?\n\nLena Medical Telehealth is a fully online healthcare service that connects you with AHPRA-registered doctors, specialists, and allied health professionals via secure video consultations. The service connects patients with real, qualified Australian medical professionals for consultations, diagnoses, prescriptions, and referrals.\n\nWhether you're in Sydney, Melbourne, Canberra, regional NSW, or anywhere across Australia — if you have an internet connection, you have access to a doctor.\n\n## Telehealth Services Available\n\n### GP Consultations Online\n\nPrescription renewals, medication reviews, sick certificates, chronic disease management plan reviews, mental health consultations, sexual health, diet and lifestyle advice, specialist referrals, pathology requests, and general medical advice.\n\n### Specialist Telehealth\n\nAHPRA-registered specialists across a range of disciplines — without the long wait times and travel.\n\n### Allied Health Telehealth\n\nPhysiotherapy, nutrition, nursing, and other allied health services online. Perfect for follow-ups, rehabilitation, and ongoing care.\n\n## Why Choose Lena Medical Telehealth?\n\nAHPRA-Registered Professionals: Every doctor and health professional is fully registered. You're always in safe, qualified hands.\n\nBulk Billing Available: Many consultations are bulk billed — no out-of-pocket costs for eligible Medicare card holders.\n\nAustralia-Wide Coverage: From capital cities to regional and remote communities. No matter where you live, quality healthcare is a click away.\n\nConvenient and Private: No travel, no waiting rooms, no time off work. Consult from anywhere — your couch, your office, or your car.\n\nSecure Platform: Meets Australian privacy and security standards. Your health information is protected.\n\n## Who Benefits Most from Telehealth?\n\n- Rural and regional Australians who may be hours from the nearest GP or specialist.\n\n- Busy professionals and parents who struggle to fit clinic visits into their day.\n\n- Elderly patients and those with mobility challenges who find travel difficult.\n\n- People managing chronic conditions who need regular check-ins.\n\n- Anyone needing a quick consultation — prescription renewal, sick certificate, or medical advice.\n\n## When Should You Visit a Clinic Instead?\n\nSome things need hands-on care: physical examinations, blood tests, vaccinations, wound care, skin cancer screening with dermoscopy. Patients in the Tea Gardens, Hawks Nest, or Hunter Valley area can visit Tea Gardens GP for in-person care. Lena Medical complements these local services through its telehealth partnership with our practice.\n\n## How to Book\n\n- Visit lenamedical.com.au\n\n- Choose your consultation type — GP, specialist, or allied health.\n\n- Pick a time that works for you.\n\n- Connect via secure video from any device.\n\n## Serving All of Australia\n\nNSW — Sydney, Newcastle, Wollongong, Central Coast, Hunter Valley, Tea Gardens, Hawks Nest, and regional NSW.\n\nVIC — Melbourne, Geelong, Ballarat, Bendigo, and regional VIC.\n\nACT — Canberra and surrounds.\n\nQLD — Brisbane, Gold Coast, Sunshine Coast, and regional QLD.\n\nAnd every other state and territory.\n\n## Book Your Online Consultation Today\n\n🌐 lenamedical.com.au\n\nAHPRA-Registered Doctors | Bulk Billing Available | Australia-Wide\n\nPrefer an in-person visit?\n\nTea Gardens GP and Lena Medical work in partnership to give patients access to both in-person and telehealth care. For hands-on services such as skin cancer checks, pathology, vaccinations, and comprehensive health assessments, book directly with Tea Gardens GP.\n\nLena Medical Telehealth — Quality healthcare, wherever you are."
},
{
    "id": 18,
    "title": "Aboriginal and Torres Strait Islander Health Services in Tea Gardens",
    "excerpt": "Culturally safe health services for Aboriginal and Torres Strait Islander communities in Tea Gardens NSW. Call 02 7241 7750.",
    "image": "/images/indigenous-health-tea-gardens.png",
    "date": "22 August 2026",
    "author": "Tea Garden GP Team",
    "category": "General Practice",
    "tags": ["Tea Gardens", "health"],
    "metaTitle": "Aboriginal Health Services Tea Gardens NSW | Culturally Safe GP | Tea Gardens GP",
    "metaDescription": "Culturally safe health services for Aboriginal and Torres Strait Islander communities in Tea Gardens NSW. Call 02 7241 7750.",
    "content": "At Tea Gardens GP, we are committed to providing culturally safe, respectful healthcare for Aboriginal and Torres Strait Islander peoples. We acknowledge the Worimi people as the traditional custodians of the land on which our clinic stands, and we pay our respects to Elders past, present, and emerging.\n\n## Our Culturally Safe Approach\n\nWe take the time to listen, understand, and work with you. We respect cultural practices and beliefs, offer flexibility in appointments, ensure privacy and confidentiality, and work collaboratively with Aboriginal health workers and community organisations.\n\n## Aboriginal and Torres Strait Islander Health Checks\n\nMedicare funds a comprehensive annual health check for Aboriginal and Torres Strait Islander patients of all ages. Completely free (bulk billed), covering physical health, mental and emotional wellbeing, ear and eye health, chronic disease screening, immunisations, nutrition, and specialist referrals.\n\n## Closing the Gap\n\nWe support the Closing the Gap framework by providing Medicare-funded health checks and care plans, coordinating with local Aboriginal health services, and ensuring our clinic is a welcoming environment.\n\n## Book Your Appointment\n\n📞 Call 02 7241 7750\n\n📍 Tea Gardens, NSW 2324\n\nNeed to see a doctor online?\n\nFor telehealth and virtual GP consultations anywhere in Australia, visit our sister service Lena Medical Telehealth — AHPRA-registered online doctors available from the comfort of your home.\n\nTea Gardens GP — Culturally safe care, grounded in respect."
},
{
    "id": 17,
    "title": "Chronic Pain Management in Tea Gardens: Finding Relief, Restoring Life",
    "excerpt": "Expert chronic pain management at Tea Gardens GP. Back pain, arthritis & nerve pain therapy. Call 02 7241 7750.",
    "image": "/images/chronic-pain-management-tea-gardens.png",
    "date": "22 August 2026",
    "author": "Tea Garden GP Team",
    "category": "General Practice",
    "tags": ["Tea Gardens", "health"],
    "metaTitle": "Chronic Pain Management Tea Gardens NSW | Pain Therapy | Tea Gardens GP",
    "metaDescription": "Expert chronic pain management at Tea Gardens GP. Back pain, arthritis & nerve pain therapy. Call 02 7241 7750.",
    "content": "Chronic pain changes everything. If you've been living with persistent pain, you know how exhausting it can be. At Tea Gardens GP, we take chronic pain seriously.\n\n## What Is Chronic Pain?\n\nPain persisting more than three months, caused by arthritis, back injuries, nerve damage, fibromyalgia, or sometimes without obvious cause.\n\n## Our Whole-Person Approach\n\n- Medical Assessment: Thorough evaluation of pain location, severity, triggers, and daily impact.\n\n- Medication Management: Evidence-based prescribing with minimal side effects.\n\n- Allied Health Coordination: Physio, exercise physiology, and psychology referrals via Team Care Arrangements with Medicare rebates.\n\n- Lifestyle Guidance: Sleep, exercise, stress, and nutrition advice tailored to your condition.\n\n- Mental Health Support: Integrated support including Mental Health Care Plans.\n\n## Conditions We Treat\n\nLower back pain, sciatica, osteoarthritis, rheumatoid arthritis, neck and shoulder pain, neuropathic pain, fibromyalgia, chronic headaches and migraines, post-surgical pain.\n\n## Book Your Consultation\n\n📞 Call 02 7241 7750\n\n📍 Tea Gardens, NSW 2324\n\nNeed to see a doctor online?\n\nFor telehealth and virtual GP consultations anywhere in Australia, visit our sister service Lena Medical Telehealth — AHPRA-registered online doctors available from the comfort of your home.\n\nTea Gardens GP — Your partner in managing chronic pain."
},
{
    "id": 16,
    "title": "Elderly Care Services in Tea Gardens: Ageing Well on the Myall Coast",
    "excerpt": "Specialised elderly care in Tea Gardens NSW. 75+ health assessments, medication management. Bulk billing. Call 02 7241 7750.",
    "image": "/images/elderly-care-tea-gardens.png",
    "date": "22 August 2026",
    "author": "Tea Garden GP Team",
    "category": "General Practice",
    "tags": ["Tea Gardens", "health"],
    "metaTitle": "Elderly Care GP Tea Gardens NSW | 75+ Assessments | Tea Gardens GP",
    "metaDescription": "Specialised elderly care in Tea Gardens NSW. 75+ health assessments, medication management. Bulk billing. Call 02 7241 7750.",
    "content": "Growing older doesn't mean giving up independence — it means having the right support. At Tea Gardens GP, we provide specialised elderly care that respects your dignity and supports your independence.\n\n## 75+ Health Assessments\n\nMedicare-funded annual assessment covering physical and mental health, medications, mobility, falls risk, nutrition, and social wellbeing. Bulk billed.\n\n## Medication Management\n\nRegular medication reviews to check for interactions, side effects, and necessity.\n\n## Chronic Disease Care\n\nPersonalised care plans for diabetes, arthritis, heart disease, and osteoporosis.\n\n## Cognitive Health\n\nCognitive assessments and referrals to support services and specialists.\n\n## Falls Prevention\n\nFalls risk assessment and strategies to keep you safe at home.\n\n## Supporting Carers and Families\n\nHome Care Package assessments, My Aged Care referrals, and respite care coordination.\n\n## Book Your Appointment\n\n📞 Call 02 7241 7750\n\n📍 Tea Gardens, NSW 2324\n\nNeed to see a doctor online?\n\nFor telehealth and virtual GP consultations anywhere in Australia, visit our sister service Lena Medical Telehealth — AHPRA-registered online doctors available from the comfort of your home.\n\nTea Gardens GP — Supporting you at every stage of life."
},
{
    "id": 15,
    "title": "Children's Health Services in Tea Gardens: Growing Up Healthy on the Myall Coast",
    "excerpt": "Expert children",
    "image": "/images/childrens-health-tea-gardens.png",
    "date": "22 August 2026",
    "author": "Tea Garden GP Team",
    "category": "General Practice",
    "tags": ["Tea Gardens", "health"],
    "metaTitle": "Children's Doctor Tea Gardens NSW | Kids Health & Immunisation | Tea Gardens GP",
    "metaDescription": "Expert children",
    "content": "From first immunisation to first day of school — your child's health journey is full of milestones. At Tea Gardens GP, we provide warm, professional paediatric care at every stage.\n\n## Newborn and Infant Care\n\nFeeding advice, weight monitoring, postnatal check-ups, and early health assessments.\n\n## Childhood Immunisations\n\nWe follow the National Immunisation Program schedule. All childhood vaccinations are free and bulk billed.\n\n## Developmental Checks\n\nWe monitor speech, motor skills, social and emotional growth, and arrange referrals if needed.\n\n## School Health Checks\n\nVision, hearing, immunisation status, and general health assessments before school starts.\n\n## Childhood Illness\n\nEar infections, coughs, fevers, rashes — our GPs diagnose and treat common childhood illnesses quickly.\n\n## A Child-Friendly Clinic\n\nWe make kids feel comfortable and safe, explaining everything in kid-friendly language.\n\n## Book Your Child's Appointment\n\n📞 Call 02 7241 7750\n\n📍 Tea Gardens, NSW 2324\n\nNeed to see a doctor online?\n\nFor telehealth and virtual GP consultations anywhere in Australia, visit our sister service Lena Medical Telehealth — AHPRA-registered online doctors available from the comfort of your home.\n\nTea Gardens GP — Where kids feel welcome and parents feel reassured."
},
{
    "id": 14,
    "title": "Comprehensive Health Checks in Tea Gardens: Prevention Is the Best Medicine",
    "excerpt": "Book a comprehensive health check at Tea Gardens GP. Full preventive screening. Bulk billing available. Call 02 7241 7750.",
    "image": "/images/comprehensive-health-check-tea-gardens.png",
    "date": "22 August 2026",
    "author": "Tea Garden GP Team",
    "category": "General Practice",
    "tags": ["Tea Gardens", "health"],
    "metaTitle": "Health Check Tea Gardens NSW | Full Check-Up & Preventive Screening | Tea Gardens GP",
    "metaDescription": "Book a comprehensive health check at Tea Gardens GP. Full preventive screening. Bulk billing available. Call 02 7241 7750.",
    "content": "How long has it been since your last full health check? At Tea Gardens GP, we believe prevention is always better than cure — and a comprehensive health check is the best way to stay ahead of potential health issues.\n\n## What's Included?\n\nBlood pressure monitoring, cholesterol and lipid profile, blood glucose screening, kidney and liver function tests, thyroid screening, heart health assessment, weight and BMI evaluation, skin cancer awareness check, mental health screening, immunisation review, and age-appropriate cancer screenings.\n\n## Who Should Get a Health Check?\n\nUnder 40: Every two to three years. 40–49: Every one to two years. 50+: Annual checks recommended. 75+: Medicare funds a comprehensive annual assessment.\n\n## The Medicare 45–49 Health Check\n\nMedicare funds a one-off preventive health check for ages 45–49 (or 30+ for Aboriginal and Torres Strait Islander patients). It's bulk billed at Tea Gardens GP.\n\n## Book Your Health Check\n\n📞 Call 02 7241 7750\n\n📍 Tea Gardens, NSW 2324\n\nNeed to see a doctor online?\n\nFor telehealth and virtual GP consultations anywhere in Australia, visit our sister service Lena Medical Telehealth — AHPRA-registered online doctors available from the comfort of your home.\n\nTea Gardens GP — Prevention starts here."
},
{
    "id": 13,
    "title": "Men's Health Checks in Tea Gardens: It's Time to Put Your Health First",
    "excerpt": "Comprehensive men",
    "image": "/images/mens-health-tea-gardens.png",
    "date": "22 August 2026",
    "author": "Tea Garden GP Team",
    "category": "General Practice",
    "tags": ["Tea Gardens", "health"],
    "metaTitle": "Men's Health Check Tea Gardens NSW | Prostate, Heart & Mental Health | Tea Gardens GP",
    "metaDescription": "Comprehensive men",
    "content": "Let's be real — men are often the last to see a doctor. Whether it's thinking \"she'll be right\" or simply not making time, too many blokes skip the check-ups that could catch serious conditions early. At Tea Gardens GP, we're here to change that.\n\n## Why Men's Health Checks Matter\n\nAustralian men die on average 4.2 years earlier than women. One in two will be diagnosed with cancer. Heart disease is the leading killer. Men are three times more likely to die by suicide. Many of these outcomes are preventable when caught early.\n\n## What's Included?\n\nOur men's health checks cover blood pressure and cholesterol, blood sugar screening, prostate health discussion and PSA testing, heart health assessment, mental health and mood screening, weight and lifestyle review, and skin cancer awareness.\n\n## Prostate Health\n\nProstate cancer is the most commonly diagnosed cancer in Australian men. When detected early, the survival rate is excellent. A simple blood test (PSA) combined with a GP conversation gives you clarity and peace of mind.\n\n## Mental Health Is Health\n\nOur GPs provide a judgement-free space to talk about stress, anxiety, depression, or anything on your mind. We also offer Mental Health Care Plans for subsidised psychology sessions.\n\n## Book Your Check-Up\n\nNo excuses, mate. Book your check-up today.\n\n📞 Call 02 7241 7750\n\n📍 Tea Gardens, NSW 2324\n\nServing Tea Gardens, Hawks Nest, Karuah, Bulahdelah, and the Hunter Valley.\n\nNeed to see a doctor online?\n\nFor telehealth and virtual GP consultations anywhere in Australia, visit our sister service Lena Medical Telehealth — AHPRA-registered online doctors available from the comfort of your home.\n\nTea Gardens GP — Looking after the blokes of the Myall Coast."
},
{
    "id": 12,
    "title": "Women's Health Services in Tea Gardens: Compassionate Care at Every Stage",
    "excerpt": "Comprehensive women",
    "image": "/images/womens-health-tea-gardens.png",
    "date": "22 August 2026",
    "author": "Tea Garden GP Team",
    "category": "General Practice",
    "tags": ["Tea Gardens", "health"],
    "metaTitle": "Women's Health Clinic Tea Gardens NSW | Pregnancy, Screening & Care | Tea Gardens GP",
    "metaDescription": "Comprehensive women",
    "content": "Every woman's health journey is unique — and every stage of life brings different needs. At Tea Gardens GP, we provide compassionate, confidential women's health services that support you from adolescence through to menopause and beyond.\n\n## Cervical Screening (CST)\n\nThe cervical screening test is recommended every five years for women aged 25–74. It's one of the most effective ways to prevent cervical cancer. We make the process as comfortable and dignified as possible.\n\n## Pregnancy and Antenatal Care\n\nFrom confirming your pregnancy to shared antenatal care with your hospital or midwife team, our GPs provide early pregnancy assessments, routine blood work, ultrasound referrals, and ongoing support.\n\n## Contraception and Family Planning\n\nConfidential advice on all contraception options — the pill, IUDs, implants, and more — so you can make informed choices.\n\n## Menopause Management\n\nOur GPs provide evidence-based treatment options, including hormone replacement therapy (HRT), to help you navigate menopause comfortably.\n\n## Breast Health\n\nClinical breast examinations and referrals for mammograms and BreastScreen NSW.\n\n## Why a Dedicated Women's Health GP Matters\n\nHaving a GP who listens without judgement, takes your symptoms seriously, and provides a safe space makes all the difference. We build long-term relationships with our female patients — understanding your history, concerns, and goals.\n\n## Book Your Appointment\n\n📞 Call 02 7241 7750\n\n📍 Tea Gardens, NSW 2324\n\nServing Tea Gardens, Hawks Nest, Karuah, and the wider Hunter Valley.\n\nNeed to see a doctor online?\n\nFor telehealth and virtual GP consultations anywhere in Australia, visit our sister service Lena Medical Telehealth — AHPRA-registered online doctors available from the comfort of your home.\n\nTea Gardens GP — Where women's health is treated with care and respect."
},
{
    "id": 11,
    "title": "Chronic Disease Management Plans in Tea Gardens: Your Guide to Better Health",
    "excerpt": "Get a personalised chronic disease management plan at Tea Gardens GP. Expert care for diabetes, heart disease & hypertension. Medicare bulk billing available.",
    "image": "/images/chronic-disease-management-tea-gardens.png",
    "date": "22 August 2026",
    "author": "Tea Garden GP Team",
    "category": "General Practice",
    "tags": ["Tea Gardens", "health"],
    "metaTitle": "Chronic Disease Management Plan Tea Gardens | Diabetes & Heart Care | Tea Gardens GP",
    "metaDescription": "Get a personalised chronic disease management plan at Tea Gardens GP. Expert care for diabetes, heart disease & hypertension. Medicare bulk billing available.",
    "content": "Living with a chronic condition like diabetes, heart disease, or asthma can feel overwhelming — but you don't have to manage it alone. At Tea Gardens GP, our Chronic Disease Management Program gives you a clear, personalised plan that helps you stay on top of your health, with the full support of your local GP team.\n\n## What Is a Chronic Disease Management Plan?\n\nA GP Management Plan (GPMP) is a structured care plan created by your doctor to help you manage an ongoing health condition. Under Medicare, if you have a chronic condition that has lasted or is expected to last six months or more, you're eligible for a management plan — and it can be bulk billed at Tea Gardens GP.\n\nYour plan may also include a Team Care Arrangement (TCA), which coordinates your care across multiple health professionals — such as dietitians, physiotherapists, or diabetes educators — with Medicare rebates for up to five allied health visits per year.\n\n## Conditions We Manage\n\nOur GPs create management plans for diabetes (Type 1 and Type 2), cardiovascular disease and hypertension, COPD, asthma, arthritis, chronic kidney disease, mental health conditions, and osteoporosis.\n\n## How It Works\n\n- Assessment: A comprehensive review of your condition, lifestyle, and health goals.\n\n- Your Plan: A personalised management plan with clear goals, medications, lifestyle changes, and follow-up schedules.\n\n- Team Care: Coordination with allied health professionals in the Hunter region — often at no out-of-pocket cost.\n\n- Regular Reviews: Your plan is reviewed every three to six months and adjusted as your needs change.\n\n## Why Choose Tea Gardens GP?\n\nWe're not a large impersonal clinic — we're your neighbours. Our team knows your name, your history, and your goals. With on-site pathology and a strong local allied health network, everything you need is close to home.\n\n## Book Your Appointment\n\n📞 Call 02 7241 7750\n\n📍 Tea Gardens, NSW 2324\n\nServing Tea Gardens, Hawks Nest, Karuah, Bulahdelah, and the wider Hunter region.\n\nNeed to see a doctor online?\n\nFor telehealth and virtual GP consultations anywhere in Australia, visit our sister service Lena Medical Telehealth — AHPRA-registered online doctors available from the comfort of your home.\n\nTea Gardens GP — Comprehensive chronic disease care, close to home."
},

    {
      id: 1,
      title: 'High Blood Cholesterol in Men: A Silent Threat to Your Health',
      excerpt: 'High Blood Cholesterol in men often develops quietly, without symptoms, making it a silent threat to cardiovascular health.',
      content: `High cholesterol is a major risk factor for heart disease and stroke. Many men are unaware they have high cholesterol because it typically has no symptoms.

## Understanding Cholesterol
Cholesterol is a waxy substance found in your blood. Your body needs cholesterol to build healthy cells, but high levels can increase your risk of heart disease.

## Risk Factors for Men
- Age (risk increases after 45)
- Family history of heart disease
- Poor diet high in saturated fats
- Lack of physical activity
- Smoking and obesity

## Prevention and Treatment
- Eat a heart-healthy diet
- Exercise regularly
- Maintain a healthy weight
- Don't smoke
- Get regular cholesterol screenings

Contact Tea Gardens general practitioners to schedule your cholesterol screening today.`,
      image: '/images/blood-pressure-blog.jpg',
      date: '2025-07-25',
      author: 'Tea Garden GP Team',
      category: 'mens-health',
      tags: ['cholesterol', 'men\'s health', 'heart disease']
    },
    {
      id: 2,
      title: 'Kidney Care Tips: Better Kidney Health',
      excerpt: 'Maintaining kidney health is essential for overall well-being. The kidneys filter waste and excess water from your blood.',
      content: `Your kidneys work 24/7 to keep your body healthy. Simple lifestyle changes can help maintain optimal kidney function.

## Essential Kidney Care Tips
1. **Stay Hydrated** - Drink 8-10 glasses of water daily
2. **Eat a Balanced Diet** - Limit sodium and processed foods
3. **Exercise Regularly** - Helps maintain healthy blood pressure
4. **Monitor Blood Pressure** - High blood pressure can damage kidneys
5. **Avoid Overuse of Pain Medications** - NSAIDs can harm kidneys

## Warning Signs
Contact your general practitioners if you experience:
- Changes in urination
- Swelling in legs or feet
- Fatigue or weakness
- Shortness of breath

Regular check-ups at Tea Gardens general practitioners can help detect kidney problems early.`,
      image: '/images/kidney-blog.webp',
      date: '2025-07-20',
      author: 'Tea Garden GP Team',
      category: 'prevention',
      tags: ['kidney health', 'prevention', 'wellness']
    },
    {
      id: 3,
      title: 'Skin Cancer: Early Detection Symptom Quick Guide',
      excerpt: 'Skin cancer develops when skin cells grow uncontrollably. Early detection is crucial for successful treatment.',
      content: `Australia has one of the highest rates of skin cancer in the world. Regular skin checks and knowing the ABCDE rule can help detect skin cancer early.

## The ABCDE Rule for Melanoma Detection
**A - Asymmetry**: One half doesn't match the other
**B - Border**: Irregular or poorly defined edges
**C - Color**: Varied colors within the same mole
**D - Diameter**: Larger than 6mm (pencil eraser size)
**E - Evolving**: Changes in size, shape, color, or texture

## Prevention Tips
- Use broad-spectrum SPF 30+ sunscreen
- Seek shade during peak hours (10am-4pm)
- Wear protective clothing and hats
- Perform monthly self-exams
- Schedule regular skin checks

Tea Gardens general practitioners offers comprehensive skin cancer screenings. Early detection saves lives.`,
      image: '/images/skin-cancer-check-tea-gardens-featured.png',
      date: '2025-07-15',
      author: 'Tea Garden GP Team',
      category: 'prevention',
      tags: ['skin cancer', 'early detection', 'prevention']
    },
    {
      id: 4,
      title: 'Your Guide to a Healthy and Happy Pregnancy',
      excerpt: 'Important tips for Mothers To Be, for a Healthy and Happy Pregnancy.',
      content: `Pregnancy is an exciting time filled with changes and preparations. Proper prenatal care can ensure a healthy pregnancy.

## Prenatal Care Essentials
### First Trimester (Weeks 1-12)
- Schedule your first prenatal appointment
- Begin taking prenatal vitamins with folic acid
- Avoid alcohol, smoking, and certain medications
- Eat a balanced diet with adequate protein

### Second Trimester (Weeks 13-26)
- Continue regular prenatal checkups
- Consider genetic screening tests
- Monitor weight gain
- Start preparing for childbirth classes

### Third Trimester (Weeks 27-40)
- Increase prenatal visit frequency
- Monitor baby's movements
- Prepare your birth plan
- Pack your hospital bag

## Nutrition During Pregnancy
Essential nutrients include folic acid, iron, calcium, and protein. Avoid raw meats, high-mercury fish, and alcohol.

Tea Gardens general practitioners provides comprehensive prenatal care throughout your pregnancy journey.`,
      image: 'https://images.unsplash.com/photo-1555252333-9f8e92e65df9?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      date: '2025-07-10',
      author: 'Tea Garden GP Team',
      category: 'womens-health',
      tags: ['pregnancy', 'prenatal care', 'women\'s health']
    },
    {
      id: 5,
      title: 'Cancer in Women: Early Detection and Prevention',
      excerpt: 'Cancer ranks among the top health challenges facing Australians today. Learn about early detection and prevention.',
      content: `Early detection significantly improves cancer treatment outcomes. Regular screenings can save lives through early intervention.

## Common Cancers in Women
### Breast Cancer
- Monthly self-exams
- Clinical breast exams
- Mammograms every 2 years from age 50

### Cervical Cancer
- HPV vaccination
- Cervical screening test every 5 years (ages 25-74)

### Ovarian Cancer
Warning signs include persistent bloating, pelvic pain, and urinary urgency.

## General Prevention Strategies
- Maintain a healthy weight
- Exercise regularly
- Eat a balanced diet
- Limit alcohol consumption
- Don't smoke
- Protect skin from UV radiation

Contact Tea Gardens general practitioners to discuss your screening schedule and individual risk factors.`,
      image: '/images/cancer-in-women-blog.webp',
      date: '2025-07-05',
      author: 'Tea Garden GP Team',
      category: 'womens-health',
      tags: ['cancer prevention', 'women\'s health', 'screening']
    },
    {
      id: 6,
      title: 'Why Tea Gardens GPs are Essential for Your Well-being',
      excerpt: 'The value of general practitioners (GPs) cannot be diminished when it comes to maintaining your health and well-being.',
      content: `General practitioners serve as the first point of contact in the general practitioners system. They provide comprehensive care, preventive services, and coordinate with specialists when needed.

## The Role of a General Practitioner
GPs are medical doctors who provide primary general practitioners services to patients of all ages. They are trained to diagnose and treat a wide range of health conditions.

## Why Choose Tea Gardens general practitioners?
### Comprehensive Care
Our GPs provide holistic general practitioners that considers your physical, mental, and social well-being.

### Continuity of Care
Building a long-term relationship with your GP means better understanding of your health history and more personalized care.

### Preventive Focus
We emphasize prevention through regular health checkups, age-appropriate screenings, and lifestyle counseling.

Contact Tea Gardens general practitioners today to establish your general practitioners partnership.`,
      image: '/images/mediaTwo.jpg',
      date: '2025-06-30',
      author: 'Tea Garden GP Team',
      category: 'general-practice',
      tags: ['general practice', 'general practitioners', 'wellness']
    },
    {
      id: 7,
      title: 'Why Tea Gardens general practitioners shine bright in Australia',
      excerpt: 'Everyone knows that Australia gives its people and permanent residents access to quality general practitioners. Learn what makes Tea Gardens general practitioners stand out.',
      content: `Our commitment to patient-centered care, modern facilities, and experienced general practitioners makes us a leading choice for families in the Tea Gardens area.

## Australia's General Practitioners System
Australia is renowned for having one of the world's best general practitioners systems, combining universal general practitioners coverage through Medicare with high-quality medical training.

## What Makes Tea Gardens general practitioners special
### Patient-Centered Approach
We put patients at the center of everything we do with personalized treatment plans and respectful, compassionate care.

### Experienced Medical Team
Our general practitioners bring extensive medical training, years of clinical experience, and dedication to excellence.

### Modern Facilities
Our clinic features state-of-the-art medical equipment, comfortable environment, and advanced diagnostic capabilities.

Experience the Tea Gardens general practitioners difference today.`,
      image: '/images/mediaThree.jpg',
      date: '2025-06-25',
      author: 'Tea Garden GP Team',
      category: 'general-practice',
      tags: ['general practitioners quality', 'patient care', 'community health']
    },
    {
      id: 8,
      title: 'How to Choose the Right Medical Doctor in Tea Gardens',
      excerpt: 'Choosing a medical doctor is an important life decision that can significantly impact your health and well-being.',
      content: `When selecting a GP, consider factors like qualifications, experience, communication style, and whether they understand your health needs and concerns.

## Why Choosing the Right Doctor Matters
Your relationship with your doctor is one of the most important general practitioners decisions you'll make.

## Key Factors to Consider
### 1. Qualifications and Credentials
Look for doctors who have medical degrees from accredited institutions and current medical registration.

### 2. Experience and Expertise
Consider years in practice, experience with your age group, and specialization in relevant areas.

### 3. Communication Style
Your doctor should listen actively, explain clearly, and show empathy and understanding.

### 4. Practice Philosophy
Look for alignment with preventive care approach and patient-centered care.

Contact Tea Gardens general practitioners to find the right doctor for your family.`,
      image: '/images/choose-right-doctor.jpeg',
      date: '2025-06-20',
      author: 'Tea Garden GP Team',
      category: 'general-practice',
      tags: ['choosing a doctor', 'general practitioners decisions', 'patient care']
    },
    {
      id: 9,
      title: 'GP Tips and Insights from Tea Gardens Medical Doctors',
      excerpt: 'Our experienced medical team shares valuable health tips and insights to help you maintain optimal health and prevent common health issues.',
      content: `Prevention is better than cure. Our doctors share practical advice on nutrition, exercise, stress management, and regular health screenings.

## Essential Health Tips from Our Medical Team

### Dr. Ahmed Shetia - General Practice & Chronic Disease Management
"With over 15 years of experience, I've learned that the foundation of good health starts with simple daily habits. Small, consistent changes lead to significant health improvements over time."

**Top 3 Tips:**
- **Stay Hydrated**: Aim for 8-10 glasses of water daily
- **Move More**: Take a 10-minute walk after each meal
- **Sleep Well**: Maintain a consistent sleep schedule

### Dr. Mohammed Mohammed - Skin Cancer Surgery & Pain Management
"With 10 years of medical experience and Fellowship with ACRRM, I focus on early detection and surgical treatment of skin cancers, combined with comprehensive pain management approaches."

**Specialized Care Areas:**
- **Skin Cancer Surgery**: Advanced surgical techniques for skin cancer treatment
- **Point-of-Care Ultrasonography**: Immediate diagnostic imaging during consultations
- **Pain Management**: Comprehensive approaches to chronic and acute pain relief

### Dr. Shaimaa Elgayar - Dermatology & Women's Health
"With nearly two decades of international medical experience and RACGP Fellowship, I believe true healing begins when we listen, understand, and treat each patient as a whole person—not just a condition."

**Specialized Expertise:**
- **Skin Cancer Medicine**: Advanced dermatology training with RACGP Certificate
- **Women's Health**: Comprehensive care for all stages of women's lives
- **Aesthetic Medicine**: Professional cosmetic and dermatological treatments

### Dr. Karan Sian - GP Registrar & Surgical Care
"With over 15 years of medical experience in intensive care, emergency medicine, and surgery, I bring comprehensive surgical skills to general practice."

**Surgical & Emergency Care:**
- **Skin Cancer Surgery**: Advanced surgical techniques with precision and care
- **Emergency Medicine**: Quick assessment and treatment of urgent conditions
- **Intensive Care**: Critical care experience for complex medical situations

### Dr. Ahmed Elakabawey - Telehealth & Medicinal Cannabis
"Through telehealth, I can provide accessible general practitioners to patients who need it most. Medicinal cannabis offers new treatment options for chronic conditions."

**Specialized Care Areas:**
- **Chronic Pain**: Alternative treatment options including medicinal cannabis
- **Mental Health**: PTSD, anxiety, and depression management
- **Accessible Care**: Quality general practitioners through telehealth consultations

## Nutrition Insights

### Balanced Diet Principles
**What to Include:**
- 5-7 servings of fruits and vegetables daily
- Whole grains over refined carbohydrates
- Lean proteins (fish, poultry, legumes)
- Healthy fats (olive oil, nuts, avocados)

**What to Limit:**
- Processed and packaged foods
- Added sugars and sweetened beverages
- Excessive sodium
- Saturated and trans fats

## Exercise and Physical Activity

### The Minimum Requirements
- **150 minutes** of moderate aerobic activity weekly
- **2 days** of strength training weekly
- **Flexibility exercises** 2-3 times weekly

### Making Exercise Enjoyable
Find activities you love: dancing, swimming, hiking, team sports, yoga, or gardening.

## Stress Management

### Healthy Coping Strategies
- **Deep Breathing**: Practice 5-10 minutes daily
- **Meditation**: Even 5 minutes can make a difference
- **Regular Exercise**: Physical activity reduces stress hormones
- **Social Connection**: Maintain relationships with family and friends
- **Adequate Sleep**: 7-9 hours for most adults

## When to See Your Doctor

### Regular Checkups
- Annual physical exams
- Age-appropriate screenings
- Chronic disease monitoring
- Medication reviews

### Warning Signs
Don't ignore:
- Persistent pain or discomfort
- Unusual fatigue or weakness
- Changes in appetite or weight
- Mood changes or depression
- Any new or concerning symptoms

## Take Action Today

Your health is your most valuable asset. Start with small changes:
1. Schedule your annual checkup
2. Increase your daily water intake
3. Add 10 minutes of walking to your routine
4. Plan one healthy meal for tomorrow
5. Set a consistent bedtime

Remember, our team at Tea Gardens general practitioners is here to support your health journey. Contact us today to discuss your individual health needs and create a personalized wellness plan.

**Prevention is always better than cure, and we're here to help you stay healthy for life.**`,
      image: '/images/GP-Tips-Insights-blog.jpg',
      date: '2025-06-15',
      author: 'Tea Garden GP Team',
      category: 'health-tips',
      tags: ['health tips', 'prevention', 'wellness']
    },
    {
        "id": 10,
        "title": "Skin Cancer Checks in Tea Gardens: Why Early Detection Saves Lives",
        "excerpt": "Living on the Myall Coast means plenty of sunshine and UV exposure. Learn when to arrange a skin cancer check and what to expect at Tea Gardens GP.",
        "content": "Australia has one of the highest rates of skin cancer in the world. Living on the beautiful Myall Coast — with its beaches at Hawks Nest and Jimmy's Beach — means we enjoy plenty of sunshine. That same sunshine also brings real risk.\n\nAt Tea Gardens GP, we believe catching skin cancer early is an important part of protecting your health.\n\n## What Is a Skin Cancer Check?\n\nA skin cancer check is a thorough examination of your skin by a trained GP. At our Tea Gardens clinic, dermoscopy may be used to examine suspicious moles, lesions and spots more closely.\n\nThe check is painless and generally takes around 20–30 minutes.\n\n## Why Skin Checks Matter on the Myall Coast\n\nOur community in Tea Gardens, Hawks Nest and the surrounding Hunter region spends a lot of time outdoors — fishing, boating, gardening and walking along the coast. This outdoor lifestyle can also mean higher cumulative UV exposure.\n\nThe three most common types of skin cancer are:\n\n- Basal cell carcinoma (BCC) — the most common type and usually slow growing.\n- Squamous cell carcinoma (SCC) — may spread if left untreated.\n- Melanoma — the most serious form, but often highly treatable when detected early.\n\n## When Should You Get a Skin Cancer Check?\n\nRegular skin checks may be recommended, particularly if you have fair skin that burns easily, a history of sunburn, many moles or freckles, a family history of skin cancer, or spend significant time outdoors.\n\nIf you notice a mole changing in size, shape, colour or texture, arrange a medical assessment promptly.\n\n## What Happens During Your Appointment?\n\nYour GP will ask about your skin history and sun exposure and examine your skin. If anything looks suspicious, they may photograph it for monitoring, arrange a biopsy, or refer you to a specialist.\n\nWe aim to make the process comfortable and respectful, with your privacy and dignity always a priority.\n\n## Protecting Your Skin Between Checks\n\n- Apply broad-spectrum SPF 50+ sunscreen and reapply as directed.\n- Wear a broad-brimmed hat and protective clothing.\n- Seek shade when UV levels are high.\n- Avoid tanning beds.\n- Check your skin regularly and look for new or changing spots.\n\n## Book Your Skin Cancer Check in Tea Gardens\n\nBook a comprehensive skin cancer check at Tea Gardens GP.\n\nPhone: 02 7241 7750\nLocation: Shop 1 and 4, 209 Myall Street, Tea Gardens NSW 2324\n\nServing Tea Gardens, Hawks Nest, Karuah, Bulahdelah and the wider Hunter region.\n\n## Need to See a Doctor Online?\n\nFor telehealth consultations across Australia, visit Lena Medical Telehealth at https://lenamedical.com.au.\n\nTea Gardens GP — your local partner in skin health and early detection.",
        "image": "/images/skin-cancer-check-tea-gardens-featured.png",
        "date": "2026-08-21",
        "author": "Tea Garden GP Team",
        "category": "prevention",
        "tags": [
            "skin cancer",
            "early detection",
            "Tea Gardens"
        ]
    }
  ];

  const post = mockPosts.find(p => p.id === parseInt(id));

  if (!post) {
    return (
      <div className="blog-page">
        <div className="container">
          <div className="blog-error">
            <h1>Article Not Found</h1>
            <p>The article you're looking for doesn't exist.</p>
            <Link to="/blog" className="btn-primary">
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="blog-page">
      <Helmet>
        <title>{post ? `${post.title} | Tea Gardens GP Blog` : 'Blog Article | Tea Gardens GP'}</title>
        <meta name="description" content={post ? post.excerpt : 'Health articles and tips from Tea Gardens GP - Your trusted medical professionals'} />
        <link rel="canonical" href={`https://teagardensgp.com.au/blog/${id}`} />
      </Helmet>
      {/* Hero Section */}
      <section className="section hero-section">
        <div className="container">
          <Link to="/blog" className="back-to-blog">
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Blog
          </Link>
          <h1>{post.title}</h1>
        </div>
      </section>

      {/* Article Content */}
      <section className="section blog-detail-content">
        <div className="container">
          <div className="blog-detail-layout">
            <article className="blog-article">
              <div className="article-header">
                <img src={post.image} alt={post.title} className="article-image" />
                <div className="article-meta">
                  <span className="article-date">
                    <FontAwesomeIcon icon={faCalendarAlt} /> {formatDate(post.date)}
                  </span>
                  <span className="article-author">
                    <FontAwesomeIcon icon={faUser} /> {post.author}
                  </span>
                </div>
              </div>
              
              <div className="article-content">
                {post.content.split('\n').map((paragraph, index) => {
                  if (paragraph.startsWith('## ')) {
                    return <h2 key={index}>{paragraph.replace('## ', '')}</h2>;
                  } else if (paragraph.startsWith('### ')) {
                    return <h3 key={index}>{paragraph.replace('### ', '')}</h3>;
                  } else if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                    return <h4 key={index}>{paragraph.replace(/\*\*/g, '')}</h4>;
                  } else if (paragraph.startsWith('- ')) {
                    return <li key={index}>{paragraph.replace('- ', '')}</li>;
                  } else if (paragraph.trim() !== '') {
                    return <p key={index}>{paragraph}</p>;
                  }
                  return null;
                })}
              </div>

              <div className="article-tags">
                <FontAwesomeIcon icon={faTag} />
                {post.tags.map((tag, index) => (
                  <span key={index} className="tag">{tag}</span>
                ))}
              </div>
            </article>

            {/* Sidebar */}
            <aside className="blog-sidebar">
              <div className="sidebar-widget">
                <h3>Related Articles</h3>
                <ul className="related-posts">
                  {mockPosts
                    .filter(p => p.id !== post.id && p.category === post.category)
                    .slice(0, 3)
                    .map(relatedPost => (
                      <li key={relatedPost.id}>
                        <Link to={`/blog/${relatedPost.id}`}>
                          <img src={relatedPost.image} alt={relatedPost.title} />
                          <div>
                            <h4>{relatedPost.title}</h4>
                            <span>{formatDate(relatedPost.date)}</span>
                          </div>
                        </Link>
                      </li>
                    ))}
                </ul>
              </div>

              <div className="sidebar-widget">
                <h3>Share This Article</h3>
                <div className="share-buttons">
                  <button className="share-btn facebook">Facebook</button>
                  <button className="share-btn twitter">Twitter</button>
                  <button className="share-btn linkedin">LinkedIn</button>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Have a Health Question?</h2>
            <p>Our general practitioners are here to help. Book an appointment today to discuss your health concerns.</p>
            <Link to="/contact" className="btn-primary">Book an Appointment</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogDetail;
